import * as React from 'react'
import * as cn from 'classnames'
import { Button, ButtonProps, Translucent } from '@amzn/dvui/dist/Button'
import { Text } from '@amzn/dvui/dist/Text'
import { Tooltip } from '@amzn/dvui/dist/Tooltip'
import { WatchParty } from '@amzn/dvui/dist/Icons/WatchParty'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import * as st from './watchPartyLink.module.scss'
import { ActionForm } from '../../utils/actionForm'
import { isTreatment } from '../../utils/utils'
import { AtfTreatment } from '../../reducers/features'
import { TextLocalizer } from '../localizer/localizer'
import { Args, withArgs } from '@utils'
import { DpWidgetContext } from '../../utils/dpWidgetContext'

type ButtonOrLinkProps = {
  buttonClassName?: string
  atfTreatment?: AtfTreatment
  isButton?: boolean
}

const ButtonOrLink = ({ isButton, atfTreatment, buttonClassName }: ButtonOrLinkProps) => {
  const atfTreatmentProps: ButtonProps = {
    ...(isTreatment(atfTreatment,'T1', 'T3') && {
      shape: 'pill',
    }),
    ...(isTreatment(atfTreatment,'T3') && {
      use: Translucent
    }),
  }

  return isButton ?
    <span className={cn({ [st.watchPartyButtonWrapper]: isTreatment(atfTreatment, 'T1', 'T2', 'T3') })}>
      <Button
        className={cn(
          st.button,
          {[st.watchPartyButton]: isTreatment(atfTreatment, 'T1', 'T2', 'T3')},
          {buttonClassName: Boolean(buttonClassName)}
        )}
        data-automation-id='watch-party-link'
        forceDoubleHeight={!isTreatment(atfTreatment, 'T1', 'T2', 'T3')}
        type="submit"
        icon={<WatchParty />}
        {...atfTreatmentProps}
      >
        <TextLocalizer stringID='DV_DP_CREATE_WATCH_PARTY'/>
      </Button>
    </span> :
    <button className={cn(st.submit, buttonClassName)} data-automation-id='watch-party-link'>
      <TextLocalizer stringID='DV_DP_CREATE_WATCH_PARTY'/>
    </button>
}

type FTUETooltipState = {
  showFTUE: boolean
}

type FTUETooltipProps = FTUEContentProps & Args

export class FTUETooltip extends React.PureComponent<FTUETooltipProps, FTUETooltipState> {
  readonly state = {
    showFTUE: false
  }

  static readonly WP_FTUE_SHOWN_COUNTER = 'dvWebWPFtueShownCounter'
  static readonly WP_FTUE_DISMISSED = 'dvWebWPFtueDismissed'
  static readonly WP_FTUE_REFTAG_PREFIX = 'atv_wp_merch_'
  private static readonly WP_FTUE_DISMISSED_VALUE = 'true'
  private static readonly WP_FTUE_MAX_TIMES_SHOWN = 3

  componentDidMount() {
    const { queryParameters } = this.props.context
    const ftueDismissed = localStorage.getItem(FTUETooltip.WP_FTUE_DISMISSED) === FTUETooltip.WP_FTUE_DISMISSED_VALUE || false
    const ftueShownCounter = parseInt(localStorage.getItem(FTUETooltip.WP_FTUE_SHOWN_COUNTER)) || 0
    const reftag = (queryParameters['ref_'] && queryParameters['ref_'][0]) || ""
    const showFTUE = (!ftueDismissed && ftueShownCounter < FTUETooltip.WP_FTUE_MAX_TIMES_SHOWN) ||
      reftag.startsWith(FTUETooltip.WP_FTUE_REFTAG_PREFIX)

    if (showFTUE) {
      const newFTUEShownCounter = ftueShownCounter + 1
      localStorage.setItem(FTUETooltip.WP_FTUE_SHOWN_COUNTER, newFTUEShownCounter.toString())
      this.setState({ showFTUE: true })
    }
  }

  onFTUEDismiss = () => {
    localStorage.setItem(FTUETooltip.WP_FTUE_DISMISSED, FTUETooltip.WP_FTUE_DISMISSED_VALUE)
  }

  render() {
    const { header, body} = this.props
    return this.state.showFTUE ?
      <div className={st.ftueContainer}>
        <Tooltip
          position="bottom"
          size="m"
          use="info"
          className={st.tooltip}
          content={<FTUEContent {...{ header, body }} />}
          startOpen
          dismissable
          onDismiss={this.onFTUEDismiss}
        >
        </Tooltip>
      </div> : null
  }
}

const FTUETooltipWithArgs = withArgs(FTUETooltip)

type FTUEContentProps = {
  header: string
  body: string
}

const FTUEContent = ({ header, body }: FTUEContentProps) => {
  return(
    <>
      <Text tag='h3' className={st.ftueHeader} string={header} />
      <Text tag='p' className={st.ftueBody} string={body} />
    </>
  )
}

export type WatchPartyLinkProps = {
  watchPartyAction: WpsRuntime.atv.wps.WatchPartyActionProps
  buttonOrLinkProps?: ButtonOrLinkProps
  withFTUETooltip?: boolean
}

export const WatchPartyLink = ({ watchPartyAction, withFTUETooltip, buttonOrLinkProps }: WatchPartyLinkProps) => {
  const { getString } = React.useContext(DpWidgetContext)
  if (!watchPartyAction) {
    return null
  }

  const { endpoint: { partialURL, query } } = watchPartyAction

  return (
    <ActionForm action={partialURL} method={'POST'} hiddenInputs={query}>
      <Tooltip
        position="top"
        size="s"
        className={st.tooltip}
        content={getString('DV_DP_WATCH_PARTY_TOOLTIP')}>
        <ButtonOrLink {...buttonOrLinkProps} />
      </Tooltip>
      {withFTUETooltip &&
        <FTUETooltipWithArgs
          header={getString('DV_DP_WATCH_PARTY_FTUE_HEADER')}
          body={getString('DV_DP_WATCH_PARTY_FTUE_BODY')}
        />
      }
    </ActionForm>
  )
}

